import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Download, Home, Calendar as CalendarIcon, DollarSign, FileText, Clock, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { format, addDays, setHours, setMinutes, isWeekend, isBefore, startOfDay } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Result = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Appointment booking states
  const [showAppointmentForm, setShowAppointmentForm] = useState(false);
  const [appointmentDate, setAppointmentDate] = useState(null);
  const [appointmentTime, setAppointmentTime] = useState('');
  const [appointmentNotes, setAppointmentNotes] = useState('');
  const [bookingAppointment, setBookingAppointment] = useState(false);
  const [appointmentBooked, setAppointmentBooked] = useState(false);
  const [appointmentSettings, setAppointmentSettings] = useState(null);

  useEffect(() => {
    fetchSubmission();
    fetchAppointmentSettings();
  }, [id]);

  const fetchAppointmentSettings = async () => {
    try {
      const response = await axios.get(`${API}/appointment-settings`);
      setAppointmentSettings(response.data);
    } catch (error) {
      console.error('Error fetching appointment settings:', error);
    }
  };

  const fetchSubmission = async () => {
    try {
      // Utiliser l'endpoint individuel au lieu de récupérer toutes les soumissions
      const response = await axios.get(`${API}/submissions/${id}`);
      if (response.data) {
        setSubmission(response.data);
      } else {
        toast.error('Soumission non trouvée');
        navigate('/');
      }
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Soumission non trouvée');
        navigate('/');
      } else {
        toast.error('Erreur lors du chargement');
        console.error(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async () => {
    try {
      const response = await axios.get(`${API}/submissions/${id}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `precontrat_${submission.customer.name.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF téléchargé avec succès');
    } catch (error) {
      toast.error('Erreur lors du téléchargement du PDF');
      console.error(error);
    }
  };

  // Get available time slots based on selected day and settings
  const getAvailableTimeSlots = (date) => {
    if (!date || !appointmentSettings) return [];
    
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayOfWeek = date.getDay();
    const dayName = dayNames[dayOfWeek];
    const dayConfig = appointmentSettings[dayName];
    
    if (!dayConfig || !dayConfig.enabled) return [];
    
    // Generate time slots based on day configuration
    const slots = [];
    const duration = appointmentSettings.duration_minutes || 90;
    const [startHour, startMin] = dayConfig.start.split(':').map(Number);
    const [endHour, endMin] = dayConfig.end.split(':').map(Number);
    
    let currentTime = startHour * 60 + startMin; // Convert to minutes
    const endTime = endHour * 60 + endMin;
    
    while (currentTime + duration <= endTime) {
      const hours = Math.floor(currentTime / 60);
      const minutes = currentTime % 60;
      const timeString = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
      slots.push(timeString);
      currentTime += duration;
    }
    
    return slots;
  };

  const isDateDisabled = (date) => {
    if (!appointmentSettings) return true;
    
    // Check if past date
    if (isBefore(date, startOfDay(new Date()))) return true;
    
    // Check if day is enabled
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayName = dayNames[date.getDay()];
    const dayConfig = appointmentSettings[dayName];
    
    return !dayConfig || !dayConfig.enabled;
  };

  const handleDateSelect = (date) => {
    setAppointmentDate(date);
    setAppointmentTime(''); // Reset time when date changes
  };

  const bookAppointment = async () => {
    if (!appointmentDate || !appointmentTime) {
      toast.error('Veuillez sélectionner une date et une heure');
      return;
    }

    setBookingAppointment(true);
    try {
      await axios.post(`${API}/appointments`, {
        submission_id: id,
        customer_name: submission.customer.name,
        customer_email: submission.customer.email,
        customer_phone: submission.customer.phone,
        appointment_date: format(appointmentDate, 'yyyy-MM-dd'),
        appointment_time: appointmentTime,
        notes: appointmentNotes
      });
      
      setAppointmentBooked(true);
      toast.success('Rendez-vous réservé avec succès!');
    } catch (error) {
      toast.error('Erreur lors de la réservation');
      console.error(error);
    } finally {
      setBookingAppointment(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-t-transparent rounded-full animate-spin mx-auto mb-4" style={{ borderColor: '#335C2E', borderTopColor: 'transparent' }}></div>
          <p style={{ color: '#666666' }}>Chargement...</p>
        </div>
      </div>
    );
  }

  if (!submission) return null;

  return (
    <div className="min-h-screen py-12 px-4" style={{ background: 'linear-gradient(180deg, #FAFAFA 0%, #F5F5F5 100%)' }}>
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-8">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center text-sm mb-4 hover:underline"
            style={{ color: '#335C2E' }}
            data-testid="back-home-btn"
          >
            <Home className="w-4 h-4 mr-1" />
            Retour à l'accueil
          </button>
          <h1 className="text-4xl sm:text-5xl font-bold mb-2" style={{ color: '#335C2E' }}>
            Votre estimation
          </h1>
          <p style={{ color: '#666666' }}>Soumission #{submission.id.slice(0, 8)}</p>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-4 justify-center mb-12">
          <button
            onClick={downloadPDF}
            className="btn-primary"
            data-testid="download-pdf-btn"
          >
            <Download className="inline-block mr-2 w-5 h-5" />
            Télécharger le PDF
          </button>
        </div>

        {/* Email Confirmation Notice */}
        <div className="mb-8 p-4 bg-green-50 border-l-4 border-green-500 rounded">
          <p className="text-green-800">
            ✉️ <strong>Un email contenant votre précontrat a été automatiquement envoyé à {submission.customer.email}</strong>
          </p>
        </div>

        {/* Pricing Summary */}
        <div className="card mb-8">
          <div className="flex items-center mb-6">
            <DollarSign className="w-6 h-6 mr-2" style={{ color: '#C7A027' }} />
            <h2 className="text-2xl font-bold" style={{ color: '#333333' }}>Estimation des coûts</h2>
          </div>
          <div className="space-y-4">
            <div className="flex justify-between items-center pb-3 border-b" style={{ borderColor: '#E5E5E5' }}>
              <span style={{ color: '#666666' }}>Base ({submission.calculation.surface} pi² × {submission.calculation.price_per_sqft} $/pi²)</span>
              <span className="font-semibold" style={{ color: '#333333' }}>{submission.calculation.subtotal.toLocaleString('fr-CA', { style: 'currency', currency: 'CAD' })}</span>
            </div>
            {submission.calculation.options_total > 0 && (
              <div className="flex justify-between items-center pb-3 border-b" style={{ borderColor: '#E5E5E5' }}>
                <span style={{ color: '#666666' }}>Options</span>
                <span className="font-semibold" style={{ color: '#333333' }}>{submission.calculation.options_total.toLocaleString('fr-CA', { style: 'currency', currency: 'CAD' })}</span>
              </div>
            )}
            <div className="flex justify-between items-center pb-3 border-b" style={{ borderColor: '#E5E5E5' }}>
              <span style={{ color: '#666666' }}>TPS (5%)</span>
              <span className="font-semibold" style={{ color: '#333333' }}>{submission.calculation.tps.toLocaleString('fr-CA', { style: 'currency', currency: 'CAD' })}</span>
            </div>
            <div className="flex justify-between items-center pb-3 border-b" style={{ borderColor: '#E5E5E5' }}>
              <span style={{ color: '#666666' }}>TVQ (9.975%)</span>
              <span className="font-semibold" style={{ color: '#333333' }}>{submission.calculation.tvq.toLocaleString('fr-CA', { style: 'currency', currency: 'CAD' })}</span>
            </div>
            <div className="flex justify-between items-center pt-4">
              <span className="text-xl font-bold" style={{ color: '#335C2E' }}>TOTAL TTC</span>
              <span className="text-2xl font-bold" style={{ color: '#C7A027' }}>{submission.calculation.total_ttc.toLocaleString('fr-CA', { style: 'currency', currency: 'CAD' })}</span>
            </div>
          </div>
        </div>

        {/* Payment Schedule */}
        <div className="card mb-8">
          <div className="flex items-center mb-6">
            <FileText className="w-6 h-6 mr-2" style={{ color: '#C7A027' }} />
            <h2 className="text-2xl font-bold" style={{ color: '#333333' }}>Échéancier de paiement</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr style={{ background: '#F5F5F5' }}>
                  <th className="text-left p-4 font-semibold" style={{ color: '#333333' }}>Étape</th>
                  <th className="text-center p-4 font-semibold" style={{ color: '#333333' }}>Pourcentage</th>
                  <th className="text-right p-4 font-semibold" style={{ color: '#333333' }}>Montant</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b" style={{ borderColor: '#E5E5E5' }}>
                  <td className="p-4" style={{ color: '#666666' }}>Dépôt</td>
                  <td className="text-center p-4" style={{ color: '#666666' }}>5%</td>
                  <td className="text-right p-4 font-semibold" style={{ color: '#335C2E' }} data-testid="payment-deposit">{submission.calculation.payment_schedule.deposit.toLocaleString('fr-CA', { style: 'currency', currency: 'CAD' })}</td>
                </tr>
                <tr className="border-b" style={{ borderColor: '#E5E5E5' }}>
                  <td className="p-4" style={{ color: '#666666' }}>Lancement</td>
                  <td className="text-center p-4" style={{ color: '#666666' }}>45%</td>
                  <td className="text-right p-4 font-semibold" style={{ color: '#335C2E' }} data-testid="payment-launch">{submission.calculation.payment_schedule.launch.toLocaleString('fr-CA', { style: 'currency', currency: 'CAD' })}</td>
                </tr>
                <tr className="border-b" style={{ borderColor: '#E5E5E5' }}>
                  <td className="p-4" style={{ color: '#666666' }}>Milieu de projet</td>
                  <td className="text-center p-4" style={{ color: '#666666' }}>30%</td>
                  <td className="text-right p-4 font-semibold" style={{ color: '#335C2E' }} data-testid="payment-middle">{submission.calculation.payment_schedule.middle.toLocaleString('fr-CA', { style: 'currency', currency: 'CAD' })}</td>
                </tr>
                <tr>
                  <td className="p-4" style={{ color: '#666666' }}>Avant livraison</td>
                  <td className="text-center p-4" style={{ color: '#666666' }}>20%</td>
                  <td className="text-right p-4 font-semibold" style={{ color: '#335C2E' }} data-testid="payment-delivery">{submission.calculation.payment_schedule.delivery.toLocaleString('fr-CA', { style: 'currency', currency: 'CAD' })}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Calendar */}
        <div className="card">
          <div className="flex items-center mb-6">
            <CalendarIcon className="w-6 h-6 mr-2" style={{ color: '#C7A027' }} />
            <h2 className="text-xl sm:text-2xl font-bold" style={{ color: '#333333' }}>Calendrier prévisionnel</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {Object.entries(submission.calculation.calendar).map(([key, date]) => {
              const labels = {
                validation: 'Validation du contrat',
                start: 'Début des travaux',
                middle: 'Milieu du projet',
                inspection: 'Inspection',
                transport: 'Transport',
                delivery: 'Livraison'
              };
              return (
                <div key={key} className="p-3 sm:p-4 rounded-lg border" style={{ borderColor: '#E5E5E5', background: '#FAFAFA' }}>
                  <p className="text-xs mb-1" style={{ color: '#999999' }}>{labels[key]}</p>
                  <p className="text-sm sm:text-base font-semibold" style={{ color: '#335C2E' }} data-testid={`calendar-${key}`}>
                    {format(new Date(date), 'PPP', { locale: fr })}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Project Details */}
        <div className="card mt-8">
          <h2 className="text-xl sm:text-2xl font-bold mb-6" style={{ color: '#333333' }}>Détails du projet</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm font-semibold mb-3" style={{ color: '#C7A027' }}>CLIENT</h3>
              <div className="space-y-1 text-sm" style={{ color: '#666666' }}>
                <p><strong>Nom:</strong> {submission.customer.name}</p>
                <p><strong>Email:</strong> {submission.customer.email}</p>
                <p><strong>Téléphone:</strong> {submission.customer.phone}</p>
              </div>
            </div>
            <div>
              <h3 className="text-sm font-semibold mb-3" style={{ color: '#C7A027' }}>PROJET</h3>
              <div className="space-y-1 text-sm" style={{ color: '#666666' }}>
                <p><strong>Type:</strong> {submission.project.project_type}</p>
                <p><strong>Modèle:</strong> {submission.project.model}</p>
                <p><strong>Surface:</strong> {submission.calculation.surface} pi²</p>
                {submission.project.options.length > 0 && (
                  <p><strong>Options:</strong> {submission.project.options.join(', ')}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Appointment Booking Section */}
        <div className="card mt-8" style={{ borderTop: '4px solid #335C2E' }}>
          <div className="text-center mb-6">
            <CalendarIcon className="w-16 h-16 mx-auto mb-4" style={{ color: '#335C2E' }} />
            <h2 className="text-xl sm:text-2xl font-bold mb-2" style={{ color: '#335C2E' }}>
              Prochaine étape : Rencontrez notre équipe
            </h2>
            <p className="text-sm sm:text-base" style={{ color: '#666666' }}>
              Réservez un rendez-vous avec l'équipe Refacing VR pour discuter de votre projet en détail
            </p>
          </div>

          {!appointmentBooked ? (
            !showAppointmentForm ? (
              <div className="text-center">
                <button
                  onClick={() => setShowAppointmentForm(true)}
                  className="btn-primary text-base sm:text-lg px-8 py-4"
                >
                  <CalendarIcon className="inline-block mr-2 w-5 h-5" />
                  Réserver un rendez-vous
                </button>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Date Selection */}
                <div>
                  <label className="block text-sm font-medium mb-3" style={{ color: '#666666' }}>
                    <CalendarIcon className="inline-block mr-2 w-4 h-4" />
                    Sélectionnez une date
                  </label>
                  <div className="flex justify-center">
                    <Popover>
                      <PopoverTrigger asChild>
                        <button className="btn-secondary px-6 py-3">
                          {appointmentDate ? format(appointmentDate, 'PPP', { locale: fr }) : 'Choisir une date'}
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="center">
                        <Calendar
                          mode="single"
                          selected={appointmentDate}
                          onSelect={handleDateSelect}
                          disabled={isDateDisabled}
                          locale={fr}
                          fromDate={new Date()}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  {appointmentSettings && (
                    <p className="text-xs mt-2 text-center" style={{ color: '#999999' }}>
                      {Object.entries(appointmentSettings)
                        .filter(([key, val]) => key !== 'duration_minutes' && val.enabled)
                        .map(([day, config]) => {
                          const dayLabels = {
                            monday: 'Lun', tuesday: 'Mar', wednesday: 'Mer',
                            thursday: 'Jeu', friday: 'Ven', saturday: 'Sam', sunday: 'Dim'
                          };
                          return `${dayLabels[day]}: ${config.start}-${config.end}`;
                        })
                        .join(' | ')}
                    </p>
                  )}
                </div>

                {/* Time Selection */}
                {appointmentDate && (
                  <div>
                    <label className="block text-sm font-medium mb-3" style={{ color: '#666666' }}>
                      <Clock className="inline-block mr-2 w-4 h-4" />
                      Choisissez un créneau horaire ({appointmentSettings?.duration_minutes || 90} min)
                    </label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {getAvailableTimeSlots(appointmentDate).map((time) => (
                        <button
                          key={time}
                          onClick={() => setAppointmentTime(time)}
                          className={`px-4 py-3 rounded-lg border-2 transition-all text-sm sm:text-base ${
                            appointmentTime === time
                              ? 'border-green-700 bg-green-50 font-semibold'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          style={appointmentTime === time ? { borderColor: '#335C2E', background: '#F0F7EE' } : {}}
                        >
                          {time}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Notes */}
                {appointmentTime && (
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#666666' }}>
                      Notes ou questions (optionnel)
                    </label>
                    <textarea
                      className="input-field w-full"
                      rows={3}
                      value={appointmentNotes}
                      onChange={(e) => setAppointmentNotes(e.target.value)}
                      placeholder="Avez-vous des questions ou des précisions à ajouter?"
                    />
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => setShowAppointmentForm(false)}
                    className="btn-secondary px-6"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={bookAppointment}
                    disabled={!appointmentDate || !appointmentTime || bookingAppointment}
                    className="btn-primary px-6"
                  >
                    {bookingAppointment ? 'Réservation...' : 'Confirmer le rendez-vous'}
                  </button>
                </div>
              </div>
            )
          ) : (
            <div className="text-center py-8">
              <CheckCircle className="w-20 h-20 mx-auto mb-4" style={{ color: '#335C2E' }} />
              <h3 className="text-xl font-bold mb-2" style={{ color: '#335C2E' }}>
                Rendez-vous confirmé !
              </h3>
              <p className="text-sm mb-4" style={{ color: '#666666' }}>
                Votre rendez-vous a été réservé pour le{' '}
                <strong>{format(appointmentDate, 'PPP', { locale: fr })}</strong> à{' '}
                <strong>{appointmentTime}</strong>
              </p>
              <p className="text-xs" style={{ color: '#999999' }}>
                Un membre de notre équipe vous contactera pour confirmer les détails.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Result;
